import type {
  ResolutionReasonCodeOption,
  ResolutionSetup
} from '@library/types/resolutions/resolutionDetails';
import { useCallback, useEffect, useState } from 'react';
import {
  useGetResolutionReasonCodeQuery,
  useUploadResolutionCSVMutation
} from '@bo-services/resolutionsAPI';
import type { ComboboxOption } from '@library/components/selects';
import { PATHS } from '@bo-constants/routes';
import { useNavigate } from 'react-router-dom';

export const useCreateResolutionConfiguration = ({
  resolutionSetup
}: {
  resolutionSetup: ResolutionSetup;
}) => {
  const [resolutionFile, setResolutionFile] = useState<File | null>(null);
  const [resolutionReason, setResolutionReason] = useState<ComboboxOption>();

  const [
    uploadResolutionCSV,
    { data, isError, isLoading, isSuccess, isUninitialized, reset }
  ] = useUploadResolutionCSVMutation();

  const { data: reasonCodeOptions = [] } = useGetResolutionReasonCodeQuery({
    reasonType: 'resolution'
  });

  const navigate = useNavigate();

  const { RESOLUTIONS } = PATHS;

  const { resolutionInputType, resolutionType } = resolutionSetup;
  const isContinueDisabled =
    !resolutionInputType ||
    !resolutionReason ||
    !resolutionType ||
    !resolutionFile ||
    isLoading;
  const isFormReady =
    resolutionFile && resolutionReason && resolutionType && resolutionInputType;

  const handleClickContinue = useCallback(async () => {
    if (isFormReady) {
      const formData = new FormData();
      formData.append('csv', resolutionFile);
      formData.append('resolution_data_type', resolutionInputType);
      formData.append('resolution_type', resolutionType);
      formData.append('reason_id', resolutionReason.value.toString());

      await uploadResolutionCSV(formData).catch(() => {});
    }
  }, [
    isFormReady,
    resolutionFile,
    resolutionInputType,
    resolutionReason,
    resolutionType,
    uploadResolutionCSV
  ]);

  const resetOnFileChange = useCallback(() => {
    if (!isUninitialized) {
      reset();
    }
  }, [isUninitialized, reset]);

  const handleResolutionFileChange = useCallback(
    (file: File | null) => {
      setResolutionFile(file);
      resetOnFileChange();
    },
    [setResolutionFile, resetOnFileChange]
  );

  const handleResolutionFileDrop = useCallback(
    (file: File | null) => {
      setResolutionFile(file);
      resetOnFileChange();
    },
    [setResolutionFile, resetOnFileChange]
  );

  const handleResolutionRemoveFile = useCallback(() => {
    setResolutionFile(null);
    resetOnFileChange();
  }, [setResolutionFile, resetOnFileChange]);

  const handleResolutionReasonChange = useCallback(
    (newResolutionReason: ComboboxOption | null) => {
      const newResolutionReasonOption =
        newResolutionReason as ResolutionReasonCodeOption;

      setResolutionReason(newResolutionReasonOption);
    },
    [setResolutionReason]
  );

  useEffect(() => {
    if (isSuccess && data) {
      navigate(`${RESOLUTIONS}/${data.resolutionId}`);
    }
  }, [isSuccess, data, navigate, RESOLUTIONS]);

  return {
    isContinueDisabled,
    isError,
    onClickContinue: handleClickContinue,
    onResolutionFileChange: handleResolutionFileChange,
    onResolutionFileDrop: handleResolutionFileDrop,
    onResolutionRemoveFile: handleResolutionRemoveFile,
    onResolutionReasonChange: handleResolutionReasonChange,
    reasonCodeOptions,
    resolutionFile,
    resolutionInputType,
    resolutionReason
  };
};
